import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  ConvertHistoryItem,
  ConvertHistoryParams,
  getConvertHistory,
} from 'api/v1/convert'
import { getPerpClosedOrders, getPerpPositionClearings } from 'api/v1/perp'
import { getSpotClosedOrders, getSpotMatchClearings } from 'api/v1/spot'
import {
  ApiHistoryItem,
  getHistoryListApi,
  HistoryType,
} from 'api/v1/txHistory'
import { uniqBy } from 'lodash'
import { RootState } from 'store'
import { updateDepositStatus } from 'store/deposit/actions'
import { DepositStatus } from 'store/deposit/types'
import { AccountId, SymbolName } from 'types'
import { MarketType } from 'views/History/OrderFilter'
import { HistoryTabType } from 'views/History/Tab'
import { CloseOrderBody, ConvertHistoryBody, TradeHistoryBody } from './types'
import toastify from '../../components/Toastify'
import {
  HistoryParams,
  TransactionsFilterParams,
  getTransactions,
} from 'api/v1/transactions'

export const updateHistoryTabAction = createAction<{ tab: HistoryTabType }>(
  'history/updateHistoryTabAction',
)

export const clearTradeHistoryAction = createAction(
  'history/clearTradeHistoryAction',
)
export const clearOrdersHistoryAction = createAction(
  'history/clearOrdersHistoryAction',
)

export const getClosedOrdersAction = createAsyncThunk<
  CloseOrderBody,
  {
    accountId: AccountId
    body: {
      range?: string
      symbol?: SymbolName
      offsetId?: number
      limit?: number
      marketType: number
      direction: string
    }
  },
  {
    state: RootState
  }
>(
  'history/getClosedOrdersAction',
  async ({ accountId, body }, { getState }) => {
    let res: any = { data: { results: [], hasMore: true, nextOffsetId: 0 } }
    if (body.marketType === MarketType.Spot) {
      res = await getSpotClosedOrders(accountId, body).catch((e) => {
        console.log(e)
        toastify.error(e.message)
      })
    } else if (body.marketType === MarketType.Perp) {
      res = await getPerpClosedOrders(accountId, body).catch((e) => {
        console.log(e)
        toastify.error(e.message)
      })
    }
    const { results: list, hasMore, nextOffsetId: offsetId } = res.data
    const state = getState()
    return {
      list: body.offsetId
        ? uniqBy([...state.history.closedOrders.list, ...list], 'id')
        : list,
      isLastPage: !hasMore,
      offsetId,
      query: body,
    }
  },
)

export const getTradeHistoryAction = createAsyncThunk<
  TradeHistoryBody,
  {
    accountId: AccountId
    body: {
      range?: string
      symbol?: SymbolName
      offsetId?: number
      limit?: number
      marketType: number
      direction: string
    }
  },
  {
    state: RootState
  }
>(
  'history/getTradeHistoryAction',
  async ({ accountId, body }, { getState }) => {
    let res: any = { data: { results: [], hasMore: true, nextOffsetId: 0 } }
    if (body.marketType === MarketType.Spot) {
      res = await getSpotMatchClearings(accountId, body)
    } else if (body.marketType === MarketType.Perp) {
      res = await getPerpPositionClearings(accountId, body)
    }
    const { results: list, hasMore, nextOffsetId: offsetId } = res.data
    const state = getState()
    return {
      list: body.offsetId
        ? uniqBy([...state.history.trades.list, ...list], 'id')
        : list,
      isLastPage: !hasMore,
      offsetId,
      query: body,
    }
  },
)

export const updateWithdrawList = createAction<ApiHistoryItem[]>(
  'history/updateWithdrawList',
)
export const updateTransferList = createAction<ApiHistoryItem[]>(
  'history/updateTransferList',
)
export const clearHistoryAction = createAction('history/clearHistoryAction')

export const getConvertHistoryAction = createAsyncThunk<
  | {
      results: ConvertHistoryItem[]
      hasMore: boolean
      nextOffsetId: number
      range: string
    }
  | undefined,
  ConvertHistoryParams,
  {
    state: RootState
  }
>('history/getConvertHistoryAction', async (body, { getState }) => {
  const state = getState()
  const accountId = state.account.accountInfo?.accountId

  if (accountId === undefined) {
    return
  }

  const res = await getConvertHistory(accountId!, body)

  const { results, nextOffsetId, hasMore, range } = res

  return {
    results: body.offsetId
      ? uniqBy([...state.history.convert.results, ...results], 'id')
      : results,
    hasMore,
    nextOffsetId,
    range,
  }
})

export const getDepositHistoryAction = createAsyncThunk<
  | {
      results: ApiHistoryItem[]
      hasMore: boolean
      nextOffsetId: number
      range: string
    }
  | undefined,
  TransactionsFilterParams,
  {
    state: RootState
  }
>('history/getDepositHistoryAction', async (params, { getState, dispatch }) => {
  const { offsetId } = params
  const state = getState()
  const accountId = state.account.accountInfo?.accountId
  if (accountId === undefined) {
    return
  }
  const {
    results = [],
    hasMore = false,
    nextOffsetId = 0,
    range = '',
  } = (await getTransactions({
    ...params,
    type: HistoryType.Deposit,
    accountId: accountId!,
  }).then((r) => r.data)) ?? {}

  // The ZKEX records always succeed, so we can update the status here.
  const batchStatus = results.map((item) => ({
    ethHash: item.ethHash!, // ethHash is existed in deposit history
    status: DepositStatus.ZkexConfirmed,
  }))
  dispatch(updateDepositStatus(batchStatus))

  return {
    results:
      offsetId > 0 ? [...state.history.deposit.results, ...results] : results,
    hasMore,
    nextOffsetId,
    range,
  }
})

export const getWithdrawHistoryAction = createAsyncThunk<
  | {
      results: ApiHistoryItem[]
      hasMore: boolean
      nextOffsetId: number
      range: string
    }
  | undefined,
  TransactionsFilterParams,
  {
    state: RootState
  }
>('history/getWithdrawHistoryAction', async (params, { getState }) => {
  const { offsetId } = params
  const state = getState()
  const accountId = state.account.accountInfo?.accountId
  if (accountId === undefined) {
    return
  }
  const {
    results = [],
    hasMore = false,
    nextOffsetId = 0,
    range = '',
  } = (await getTransactions({
    ...params,
    type: HistoryType.Withdraw,
    accountId: accountId!,
  }).then((r) => r.data)) ?? {}

  return {
    results:
      offsetId > 0 ? [...state.history.withdraw.results, ...results] : results,
    hasMore,
    nextOffsetId,
    range,
  }
})

export const getTransferHistoryAction = createAsyncThunk<
  | {
      results: ApiHistoryItem[]
      hasMore: boolean
      nextOffsetId: number
      range: string
    }
  | undefined,
  TransactionsFilterParams,
  {
    state: RootState
  }
>('history/getTransferHistoryAction', async (params, { getState }) => {
  const { offsetId } = params
  const state = getState()
  const accountId = state.account.accountInfo?.accountId
  if (accountId === undefined) {
    return
  }
  const {
    results = [],
    hasMore = false,
    nextOffsetId = 0,
    range = '',
  } = (await getTransactions({
    ...params,
    type: HistoryType.Transfer,
    accountId: accountId!,
  }).then((r) => r.data)) ?? {}

  return {
    results:
      offsetId > 0 ? [...state.history.transfer.results, ...results] : results,
    hasMore,
    nextOffsetId,
    range,
  }
})
